import { useState, type ReactElement } from "react";
import { useParams } from "react-router-dom";
import { usePortal, findService } from "../state/PortalContext.js";
import { PageHelp } from "../layout/PageHelp.js";

/** Link a service username to this admin: enter username -> OTP -> verify. Grants no roles. */
export function LinkingPage(): ReactElement {
  const { api, catalog, reloadCatalog } = usePortal();
  const params = useParams();
  const serviceId = params.service!;
  const stageId = params.stage!;
  const serviceName = findService(catalog, serviceId)?.displayName ?? serviceId;

  const [username, setUsername] = useState("");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [done, setDone] = useState<{ username: string; maskedPhone?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function start(): Promise<void> {
    setError(null);
    try {
      await api.linkStart(serviceId, stageId, username);
      // The response is intentionally the same whether or not the username exists, so we cannot
      // reveal the phone here. Move to the code step unconditionally.
      setSent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start linking");
    }
  }

  async function verify(): Promise<void> {
    setError(null);
    try {
      const res = await api.linkVerify(serviceId, stageId, code);
      setDone({ username: res.username, maskedPhone: res.maskedPhone });
      await reloadCatalog();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Verification failed");
    }
  }

  return (
    <section aria-labelledby="link-title">
      <h1 id="link-title">Link {serviceName} identity ({stageId})</h1>
      <PageHelp>
        <p>
          Use this to prove which {serviceName} account is yours. We text a code to the phone number on that
          account; entering the code shows you really own it.
        </p>
        <ul>
          <li>This is optional. You do not need it to use any screen in the portal today.</li>
          <li>It gives you no extra access. What you can do depends only on the roles an admin gives you.</li>
          <li>You need your own {serviceName} account to do this. If you don&rsquo;t have one, skip this page.</li>
          <li>It applies to the {stageId} environment only.</li>
        </ul>
      </PageHelp>

      {error ? (
        <p role="alert" className="field-error">
          {error}
        </p>
      ) : null}

      {done ? (
        <p role="status">
          Linked to {done.username}
          {done.maskedPhone ? ` (verified phone ${done.maskedPhone})` : ""}.
        </p>
      ) : sent ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void verify();
          }}
        >
          <p>If that username exists, we sent a code to its account&rsquo;s verified phone. Enter it below.</p>
          <label htmlFor="code">Verification code</label>
          <p id="code-hint" className="field-hint">
            The code from the text message. If nothing arrives, check the username and try again (you can request up
            to 3 codes per hour for the same username).
          </p>
          <input
            id="code"
            value={code}
            inputMode="numeric"
            onChange={(e) => setCode(e.target.value)}
            autoComplete="one-time-code"
            aria-describedby="code-hint"
          />
          <button type="submit" disabled={!code}>
            Verify
          </button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void start();
          }}
        >
          <label htmlFor="username">{serviceName} username</label>
          <p id="username-hint" className="field-hint">
            Your own username in the {serviceName} app (not your email). We will text a code to the phone number on that
            account.
          </p>
          <input
            id="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="off"
            aria-describedby="username-hint"
          />
          <button type="submit" disabled={!username}>
            Send code
          </button>
        </form>
      )}
    </section>
  );
}
